import type { DealOptionData } from '@src/types/OTC';
import { getOtcDealUnitNotionalDisplayValue } from '@src/utils/OTC/otcDealDisplayUtils';
import {
  AssetRenderer,
  isOTCOptionSecurity,
  logger,
  Side,
  useCurrenciesContext,
  useSecuritiesContext,
} from '@talos/kyoko';
import { compact } from 'lodash-es';
import { InspectorNumberDisplay } from './InspectorNumberDisplay';
import { InspectorText } from './InspectorText';
import { QuantityDisplay } from './QuantityDisplay';
import { InspectorSectionsRenderer } from './shared/InspectorSectionsRenderer';
import type { DisplayOutputItem } from './shared/types';
import { useCounterpartyInformation } from './shared/useCounterpartyInformation';
import { useTimingInformation } from './shared/useTimingInformation';

const useOptionTradeInformation = (instrumentData: DealOptionData): DisplayOutputItem => {
  const { securitiesBySymbol } = useSecuritiesContext();
  const { currenciesBySymbol } = useCurrenciesContext();
  const dealSecurity = instrumentData.Symbol ? securitiesBySymbol.get(instrumentData.Symbol) : undefined;
  const resolvedDealSecurity = isOTCOptionSecurity(dealSecurity) ? dealSecurity : undefined;
  if (!resolvedDealSecurity) {
    logger.error(
      new Error(`Failed to get OTC option deal security for symbol ${instrumentData.Symbol ?? '(missing)'}`),
      {
        extra: {
          dealSecurity,
          resolvedDealSecurity,
        },
      }
    );
    return {
      sectionTitle: 'Deal Information',
      fields: [],
    };
  }

  const { BaseAsset, QuoteAsset, PayoffType, DeliveryAsset, PaymentCcy, Tenor, SettlementType, StrikePriceCcy } =
    resolvedDealSecurity.ProductDetails;

  const unitNotionalDisplay = getOtcDealUnitNotionalDisplayValue(instrumentData, {
    securitiesBySymbol,
    currenciesBySymbol,
  });

  return {
    sectionTitle: 'Deal Information',
    fields: compact([
      {
        fieldName: 'Underlying Asset',
        fieldValue: <AssetRenderer asset={BaseAsset} colorful />,
      },
      {
        fieldName: 'Quote Asset',
        fieldValue: <AssetRenderer asset={QuoteAsset} colorful />,
      },
      {
        fieldName: 'Delivery Asset',
        fieldValue: <AssetRenderer asset={DeliveryAsset} colorful />,
      },
      PaymentCcy != null
        ? {
            fieldName: 'Payment Currency',
            fieldValue: <AssetRenderer asset={PaymentCcy} colorful />,
          }
        : null,
      Tenor != null
        ? {
            fieldName: 'Tenor',
            fieldValue: <InspectorText>{Tenor}</InspectorText>,
          }
        : null,

      {
        fieldName: 'Side',
        fieldValue: <Side side={instrumentData.DealSide}>{instrumentData.DealSide}</Side>,
      },
      {
        fieldName: 'Option Type',
        fieldValue: <InspectorText>{resolvedDealSecurity.ProductDetails.OptionType}</InspectorText>,
      },
      {
        fieldName: 'Settlement Type',
        fieldValue: <InspectorText>{SettlementType}</InspectorText>,
      },
      {
        fieldName: 'Quantity',
        fieldValue: <QuantityDisplay instrumentData={instrumentData} />,
      },
      !instrumentData.QuantityAsset
        ? {
            fieldName: 'Unit Notional',
            fieldValue: (
              <InspectorNumberDisplay
                value={unitNotionalDisplay.value}
                currency={unitNotionalDisplay.currency}
                increment={unitNotionalDisplay.increment}
              />
            ),
          }
        : null,
      {
        fieldName: 'Notional',
        fieldValue: (
          <InspectorNumberDisplay
            value={instrumentData.DealInfo.NotionalAmount}
            currency={instrumentData.DealInfo.NotionalAsset}
          />
        ),
      },
      {
        fieldName: 'Strike Price',
        fieldValue: (
          <InspectorNumberDisplay
            value={instrumentData.DealInfo.StrikePrice}
            currency={instrumentData.DealInfo.StrikePriceCcy ?? StrikePriceCcy ?? 'USD'}
          />
        ),
      },
      {
        fieldName: 'Premium',
        fieldValue: <InspectorNumberDisplay value={instrumentData.DealInfo.Premium} currency={PaymentCcy} />,
      },
      {
        fieldName: 'Payoff Type',
        fieldValue: <InspectorText>{PayoffType}</InspectorText>,
      },
    ]),
  };
};

const useOptionPricingInformation = (instrumentData: DealOptionData): DisplayOutputItem => {
  const { securitiesBySymbol } = useSecuritiesContext();
  const dealSecurity = instrumentData.Symbol ? securitiesBySymbol.get(instrumentData.Symbol) : undefined;
  const resolvedDealSecurity = isOTCOptionSecurity(dealSecurity) ? dealSecurity : undefined;
  if (!resolvedDealSecurity) {
    return {
      sectionTitle: 'Pricing',
      fields: [],
    };
  }
  const Index = resolvedDealSecurity.ProductDetails.Index;
  return {
    sectionTitle: 'Pricing',
    fields: compact([
      Index != null
        ? {
            fieldName: 'Index',
            fieldValue: <InspectorText>{Index}</InspectorText>,
          }
        : null,
    ]),
  };
};

interface OptionInstrumentInspectorSectionsProps {
  instrumentData: DealOptionData;
}

export const OptionInstrumentInspectorSections = ({ instrumentData }: OptionInstrumentInspectorSectionsProps) => {
  const tradeInfo = useOptionTradeInformation(instrumentData);
  const pricingInfo = useOptionPricingInformation(instrumentData);
  const counterpartyInfo = useCounterpartyInformation(instrumentData);
  const timingInfo = useTimingInformation(instrumentData);

  const sections = [tradeInfo, pricingInfo, counterpartyInfo, timingInfo];

  return <InspectorSectionsRenderer sections={sections} />;
};
